import {
	useCallback,
	useEffect,
	useState,
	useMemo
} from 'react';

import {
	gridCellBehavior,
	gridHeaderCellBehavior,
	gridNestedBehavior,
	gridRowBehavior,
	Table,
	Skeleton,
	Loader
} from '@fluentui/react-northstar';

import { ComponentDesignProp } from '@fluentui/react-bindings';

import { readProperty } from 'utils';

import {
	AutoSizer,
	InfiniteLoader,
	InfiniteLoaderProps,
	List as ReactVirtualizedList,
	ListProps,
	TableProps
} from 'react-virtualized';

import {
	TableSourceOptions,
	UseInfiniteSource,
	useInfiniteSource
} from 'components/Table/Source';

import { ColumnLayout, ListLayout } from 'components/Table/Layout';

import Constants from 'components/WorkZone/Odata/Constants';
import Content from 'components/Content/Content';

import { getActions } from 'components/Table/Actions';
import { ActionableTable } from './ActionableTable';
import { ActionableCell } from './ColumnContextMenu';

import './Table.css';

// Overrides ARIA attributes assigned by default, which break accessibility
const accessibilityListProperties: Partial<ListProps> = {
	'aria-label': '',
	'aria-readonly': undefined,
	containerRole: 'presentation',
	role: 'presentation'
};

const ColumnAlignMap = {
	center: 'center',
	left: 'flex-start',
	right: 'flex-end'
};

type ExtendedComponentDesignProp = ComponentDesignProp & { justifyContent?: string }

type TableRowCell = {
	key: string,
	content: React.ReactNode,
	truncateContent?: boolean
}

export type TableRowData = {
	key: string,
	id: string | number,
	cells: { [index: string]: TableRowCell },
	data: any
}

type TableBodyCell = {
	name: string,
	layout: ColumnLayout
}

type TableDataEntry = {
	ID: string | number
}

type TableData = {
	value?: TableDataEntry[]
}

const sortByOrder = function (this: { [column: string]: ColumnLayout }, a: string, b: string) {
	return this[a].order.localeCompare(this[b].order);
};

function convertPageDataToRows(layout: ListLayout, data: any): TableRowData[] {

	return data.pages ? data.pages.reduce((acc: TableRowData[], page: TableData, pageNumber: number) => {

		const pageRows = convertDataToRows(layout, page, pageNumber);

		return [...acc, ...pageRows];

	}, []) : [];
}

function convertDataToRows(layout: ListLayout, data: TableData, page = 0): TableRowData[] {

	return data.value ? data.value.map((item: TableDataEntry, index: number) => {

		return convertRowDataToRow(layout, item, `${page}-${index}`);
	}) : [];
}

export function convertRowDataToRow(layout: ListLayout, data: TableDataEntry, key: string): TableRowData {
	const columns = Object.keys(layout.columns);

	const cells = columns.reduce((acc: { [index: string]: TableRowCell }, name) => {

		acc[name] = {
			key: `${key}-${layout.columns[name].order}`,
			truncateContent: true,
			content: null
		};

		if (typeof layout.columns[name].value === 'function') {

			const valueFunction = layout.columns[name].value as Function;

			acc[name].content = valueFunction(data);

		} else {
			acc[name].content = readProperty(name, data);
		}

		return acc;

	}, {});

	return {
		key: key,
		id: data.ID,
		cells,
		data: data
	};
}

function TableLoader() {
	return (
		<Content center style={{ height: '100vh' }}>
			<Loader />
		</Content>
	);
}

function HeaderCell({ column, source }: { column: ColumnLayout, source: UseInfiniteSource }) {

	const design = useMemo(() => {

		const { width } = column;

		const designProp: ExtendedComponentDesignProp = {};

		if (typeof width === 'number') {
			designProp.maxWidth = `${width}px`;
		}

		if (column.align) {
			designProp.justifyContent = column.align;
			if(column.align === 'right'){
				designProp.position = 'absolute';
				designProp.right = '0px';
			}
		}

		return designProp;

	}, [column]);

	const title = typeof column.label === 'string' ? column.label : column.label?.title;

	const content = typeof column.label === 'string' ? column.label : column.label?.value(source);

	return <Table.Cell truncateContent content={content} title={title} accessibility={gridHeaderCellBehavior} design={design} />;
}

function Header({ source, width }: { source: UseInfiniteSource, width: number }) {

	const { source: tableSource } = source;

	const layout = tableSource.getLayout();

	if (layout) {

		const columns = Object.keys(layout.columns).sort(sortByOrder.bind(layout.columns)).map(name => layout.columns[name]);

		return (
			<Table.Row header accessibility={gridRowBehavior} style={{ width }}>
				{columns.map(column => <HeaderCell key={column.order} column={column} source={source} />)}
			</Table.Row>
		);
	} else {
		return null;
	}
}

function BodyCell({ column, cell, actions, rowindex }: { column: TableBodyCell, cell: TableRowCell | undefined, actions?: any, rowindex: number }) {

	const design = useMemo(() => {

		const { layout } = column;

		const designProp: ExtendedComponentDesignProp = {};

		if (typeof layout.width === 'number') {
			designProp.maxWidth = `${layout.width}px`;
		}

		if (layout.align) {
			designProp.justifyContent = ColumnAlignMap[layout.align];
			if(layout.align === 'right'){
				designProp.position = 'absolute';
				designProp.right = '0px';
			}
		}

		return designProp;

	}, [column]);

	if (cell) {

		if (actions) {

			return <ActionableCell cell={cell} actions={actions} rowindex={rowindex} isActionsColumn={column.name === 'action'} trigger={(props: any) => <Table.Cell {...props} accessibility={gridCellBehavior} design={design} />} />;

		} else {

			return <Table.Cell {...cell} accessibility={gridCellBehavior} design={design} />;
		}

	} else {
		return <CellLoading column={column} />;
	}
}

function CellLoading({ column }: { column: TableBodyCell }) {

	const design = useMemo(() => {

		const { layout } = column;

		const designProp: ExtendedComponentDesignProp = {
			paddingRight: '8px',
			paddingLeft: '8px'
		};

		if (typeof layout.width === 'number') {
			designProp.maxWidth = `${layout.width}px`;
		}

		return designProp;

	}, [column]);

	return (
		<Skeleton animation="wave" design={design}>
			<Skeleton.Line />
		</Skeleton>
	);
}

function Row({ source, row, style, index }: { source: UseInfiniteSource, row: TableRowData | undefined, style: React.CSSProperties, index: number }) {

	const { source: tableSource } = source;

	const layout = tableSource.getLayout();

	if (layout) {

		const columns: TableBodyCell[] = Object.keys(layout.columns).sort(sortByOrder.bind(layout.columns)).map(name => {
			return {
				name,
				layout: layout.columns[name]
			};
		});

		return (
			<Table.Row style={style} accessibility={gridRowBehavior} aria-rowindex={index + 1}>
				{columns.map(column => {

					const actions = getActions(tableSource.collection, column.name);

					return <BodyCell key={column.layout.order} column={column} cell={row?.cells[column.name]} rowindex={index} actions={actions} />;
				})}
			</Table.Row>
		);
	} else {
		return null;
	}
}

type TableContext = {
	rows: TableRowData[],
	count: number,
	source: UseInfiniteSource,
	isLoading: boolean,
	isRowLoaded: InfiniteLoaderProps['isRowLoaded'],
	loadMoreRows: InfiniteLoaderProps['loadMoreRows'],
	rowGetter: TableProps['rowGetter'],
	rowRenderer: ListProps['rowRenderer'],
	getHeaderWidth: (width: number) => number,
	onScrollbarPresenceChange: TableProps['onScrollbarPresenceChange']
}

export function useTable(options: TableSourceOptions, customLayout: { [collection: string]: ListLayout } | undefined ): TableContext {

	const [rows, setRows] = useState<TableRowData[]>([]);

	const usedSource = useInfiniteSource(options, customLayout);

	const { data, source, isLoading, isFetching, count, fetchNextPage } = usedSource;

	const [scrollBarSize, setScrollbarSize] = useState(0);

	const [shouldDisplayLoader, updateShouldDispayLoader] = useState(true);

	const isRowLoaded = useCallback(({ index }: { index: number }) => typeof rows[index] !== 'undefined', [rows]);

	const loadMoreRows = useCallback(({ startIndex, stopIndex }: { startIndex: number, stopIndex: number }) => {

		if (!rows[startIndex] || !rows[stopIndex]) {
			return fetchNextPage();
		} else {
			return Promise.resolve();
		}

	}, [fetchNextPage, rows]);

	const rowGetter = useCallback(({ index }: { index: number }) => rows[index], [rows]);

	const rowRenderer: ListProps['rowRenderer'] = useCallback(({ index, style }) => {

		const row = rows[index];

		return <Row source={usedSource} key={row?.key || index} row={row} index={index} style={style} />;

	}, [rows, usedSource]);

	const onScrollbarPresenceChange = useCallback(({ vertical, size }) => {
		setScrollbarSize(vertical ? size : 0);
	}, []);

	const getHeaderWidth = useCallback((width: number) => width - (scrollBarSize ? scrollBarSize - 1 : scrollBarSize), [scrollBarSize]);

	const layout = useMemo(() => source.getLayout(), [source]);

	useEffect(() => {

		if (!isLoading && !isFetching) {

			if (data) {
				setRows(convertPageDataToRows(layout, data));
			}

			updateShouldDispayLoader(!data);

		} else {

			if (isLoading && isFetching) {
				updateShouldDispayLoader(true);
			}
		}

	}, [data, layout, isLoading, isFetching]);

	return {
		rows,
		count,
		source: usedSource,
		isLoading: shouldDisplayLoader,
		isRowLoaded,
		loadMoreRows,
		rowGetter,
		rowRenderer,
		getHeaderWidth,
		onScrollbarPresenceChange
	};
}

const VirtualizedTable = ({ options, flex = false, force = false, customLayout = undefined }: { options: TableSourceOptions, flex?: boolean, force?: boolean, customLayout?: { [collection: string]: ListLayout } }) => {

	const {
		rows,
		count,
		source,
		isLoading,
		isRowLoaded,
		loadMoreRows,
		rowGetter,
		rowRenderer,
		getHeaderWidth,
		onScrollbarPresenceChange
	} = useTable(options, customLayout);

	const { refetch } = source;

	useEffect(() => {
		if (force) {
			refetch();
		}
	}, [force, refetch]);

	const autoSizerStyle = useMemo(() => {

		const style: any = {
			width: 'auto'
		};

		if (flex) {
			style.flexGrow = 1;
		}

		return style;
	}, [flex]);

	const tableStyle = { height: '100%' };

	return (
		<InfiniteLoader
			isRowLoaded={isRowLoaded}
			loadMoreRows={loadMoreRows}
			minimumBatchSize={Constants.serverPageSize}
			rowCount={count}
		>
			{({ onRowsRendered, registerChild }) => (
				<AutoSizer style={autoSizerStyle}>
					{({ height, width }) => (
						<ActionableTable
							collection={options.collection}
							count={count}
							rows={rows}
							accessibility={gridNestedBehavior}
							style={tableStyle}
							source={source}
						>
							<Header source={source} width={getHeaderWidth(width)} />
							{
								isLoading
									? <TableLoader />
									: <ReactVirtualizedList
										ref={registerChild}
										rowCount={count}
										height={height}
										width={width}
										rowHeight={50}
										rowGetter={rowGetter}
										rowRenderer={rowRenderer}
										onRowsRendered={onRowsRendered}
										onScrollbarPresenceChange={onScrollbarPresenceChange}
										{...accessibilityListProperties}
									/>
							}
						</ActionableTable>
					)}
				</AutoSizer>
			)}
		</InfiniteLoader>
	);
};

export default VirtualizedTable;
